import {
  ICheckEarnedBadgesRes,
  IConsultWithECFormData,
  IGetMasteryLevelProgressDataFormData,
  IGetMasteryLevelProgressDataRes,
  IGetPresetWorksheetsFormData,
  IGetPresetWorksheetsRes,
  IGetStatisticFormData,
  IGetStatisticRes,
  IGetStudentAbGroupInfoFormData,
  IGetStudentAbGroupInfoRes,
  IGetStudentExamDetail,
  IGetStudentExamResultFilterOptionsRes,
  IGetStudentExamResultListRes,
  IGetStudentMasteryWatchTimeFormData,
  IGetStudentMasteryWatchTimeRes,
  IGetStudentScoreProgressDataFormData,
  IGetStudentScoreProgressDataRes,
  IGetStudentTopicRes,
  IGetStudentTopicsFormData,
  IGetStudentTopicsMasteryLevelFormData,
  IGetStudentTopicsMasteryLevelRes,
  IStreakProgressReq,
  IStreakProgressRes,
  IStudentBadgeRes,
  SubjectMenuListRes,
} from '@models/interfaces/student.interface';
import AUTH_API from '@services/auth.service';
import convertToFormData from '@utils/helpers/convertToFormData';
import { IDefaultResponse } from '@models/interfaces/global.interface';
import {
  IGenerateClass2SmartWorksheetReq,
  IGenerateClass2SmartWorksheetRes,
} from '@models/interfaces/worksheet.interface';

export type optionalParamProps = {
  subject?: string;
  level?: string;
  exam?: string;
};

const StudentService = {
  async subject_menu_list() {
    return await AUTH_API.post<SubjectMenuListRes>(
      `/student/subject_menu_list/json`,
    );
  },
  async consultWithEC(formData: IConsultWithECFormData) {
    return await AUTH_API.post<IDefaultResponse>(
      `student/consult_with_ec/json`,
      convertToFormData(formData),
    );
  },
  async getBadges() {
    return await AUTH_API.post<IStudentBadgeRes>(`/student/get_badges/json`);
  },

  // api for submit exam form
  async getBubbleTransactionData() {
    return await AUTH_API.post(`student/get_bubble_transaction/json`);
  },
  async getStreakProgress(formData: IStreakProgressReq = {}) {
    return await AUTH_API.post<IStreakProgressRes>(
      `student/get_streak_progress/json`,
      convertToFormData(formData),
    );
  },
  async checkEarnedBadges() {
    return await AUTH_API.post<ICheckEarnedBadgesRes>(
      `student/check_earned_badges/json`,
    );
  },
  async getStudentTopics(formData: IGetStudentTopicsFormData) {
    return await AUTH_API.post<IGetStudentTopicRes>(
      `student/topic_list/json`,
      convertToFormData(formData),
    );
  },
  async getStatistic(formData: IGetStatisticFormData) {
    return await AUTH_API.post<IGetStatisticRes>(
      `student/get_statistics/json`,
      convertToFormData(formData),
    );
  },
  async getStudentTopicsMasteryLevel(
    formData: IGetStudentTopicsMasteryLevelFormData,
  ) {
    return await AUTH_API.post<IGetStudentTopicsMasteryLevelRes>(
      `student/get_topic_list_mastery_level/json`,
      convertToFormData(formData),
    );
  },
  async getStudentScoreProgressData(
    formData: IGetStudentScoreProgressDataFormData,
  ) {
    return await AUTH_API.post<IGetStudentScoreProgressDataRes>(
      `student/get_student_progress/json`,
      convertToFormData(formData),
    );
  },
  async getMasteryLevelProgressData(
    formData: IGetMasteryLevelProgressDataFormData,
  ) {
    return await AUTH_API.post<IGetMasteryLevelProgressDataRes>(
      `student/get_mastery_level_progress/json`,
      convertToFormData(formData),
    );
  },
  async getPresetWorksheets(formData: IGetPresetWorksheetsFormData) {
    return await AUTH_API.post<IGetPresetWorksheetsRes>(
      '/parents/preset_worksheet/json',
      convertToFormData(formData),
    );
  },
  async getStudentAbGroupInfo(formData: IGetStudentAbGroupInfoFormData) {
    return await AUTH_API.post<IGetStudentAbGroupInfoRes>(
      `student/get_student_ab_group_info/json`,
      convertToFormData(formData),
    );
  },

  async getStudentExamResultList(
    count: number,
    page: number,
    param?: optionalParamProps,
  ) {
    const { subject, level, exam } = param || {};
    let queryParams = `count=${count}&page=${page}`;
    if (subject && subject !== '') queryParams += `&subject_id=${subject}`;
    if (level && level !== '') queryParams += `&level_id=${level}`;
    if (exam && exam !== '') queryParams += `&exam_type_id=${exam}`;

    return await AUTH_API.get<IGetStudentExamResultListRes>(
      `student/exam_score_list/json?${queryParams}`,
    );
  },

  async getStudentExamResultFilterOptions(type: 'level' | 'exam' | 'subject') {
    return await AUTH_API.get<IGetStudentExamResultFilterOptionsRes>(
      `student/exam_score_list_filter/json?filter_type=${type}`,
    );
  },

  async getStudentExamResultDetail(exam_score_id: string) {
    return await AUTH_API.get<IGetStudentExamDetail>(
      `student/exam_score_detail/json?exam_score_id=${exam_score_id}`,
    );
  },

  async deleteStudentExamResult(formData: FormData) {
    return await AUTH_API.post<IDefaultResponse>(
      `/student/delete_exam_score/json`,
      formData,
    );
  },

  async calculateMasteryLevel() {
    return await AUTH_API.post<IDefaultResponse>(
      `student/calculate_mastery_level/json`,
    );
  },
  async generateClass2SmartWorksheet(
    formData: IGenerateClass2SmartWorksheetReq,
  ) {
    return await AUTH_API.post<IGenerateClass2SmartWorksheetRes>(
      '/student/generate_class_to_smart_worksheet',
      convertToFormData(formData),
    );
  },
  async getStudentMasteryWatchTime(
    formData: IGetStudentMasteryWatchTimeFormData,
  ) {
    return await AUTH_API.post<IGetStudentMasteryWatchTimeRes>(
      `student/get_topic_watch_time/json`,
      convertToFormData(formData),
    );
  },
};
export default StudentService;
